import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import classNames from 'classnames'

import { getQaSelector } from '../../../../../utils/common'
import { FloatingTooltip } from '../../../../Floating'
import { ICON_HEIGHT_16, IconWithTooltip } from '../../../../Icon'
import { Link } from '../../../../Link'
import Text from '../../../../Text'
import { Widget } from '../../../../Widget'
import {
  getMatchCriteria,
  RentalPortalMatchProvenanceLegend,
} from '../../../RentalPortalMatch'
import { useApplicantProfileContext } from '../../RentalPortalApplicantProfile.hooks'
import { getApplicantMatchedListingUrl } from '../../RentalPortalApplicantProfileWidgetMatchedListing'

import {
  QA_NAME_APPLICANT_FIT_CHECK,
  QA_NAME_APPLICANT_FIT_CHECK_SCORE_LINE,
} from './ApplicantFitCheckBlock.constants'
import {
  useApplicantFitCheckReevaluate,
  useApplicantFitCheckRequest,
  useApplicantFitCheckSuggestion,
  useApplicantFitCheckValueEdit,
} from './ApplicantFitCheckBlock.hooks'
import {
  getApplicantFitCheckMatch,
  getApplicantFitCheckMetaLine,
  getApplicantFitCheckMetaTooltip,
  getApplicantFitCheckRulesUrl,
  getApplicantFitCheckSuggestion,
  getApplicantFitCheckUnavailableKey,
  getHasApplicantFitCheckRulesLink,
} from './ApplicantFitCheckBlock.utils'
import { ApplicantFitCheckBlockBreakdown } from './ApplicantFitCheckBlockBreakdown'
import {
  ApplicantFitCheckBlockRequestRow,
  getHasApplicantFitCheckRequestRow,
} from './ApplicantFitCheckBlockRequestRow'
import { ApplicantFitCheckBlockRow } from './ApplicantFitCheckBlockRow'
import { ApplicantFitCheckBlockTitle } from './ApplicantFitCheckBlockTitle'

import * as styles from './ApplicantFitCheckBlock.module.css'

import InformationIcon from '../../../../../icons/info.svg'

function ApplicantFitCheckBlock() {
  const { t } = useTranslation()

  const { applicantData, isInitiated, isPreview } = useApplicantProfileContext()

  const [isBreakdownOpen, setBreakdownOpen] = useState(false)

  const { handleAcceptSuggestion, handleDismissSuggestion, submittingField } =
    useApplicantFitCheckSuggestion()

  const { handleOpenRequestModal } = useApplicantFitCheckRequest()

  const {
    editingField,
    editValue,
    handleCancelEdit,
    handleChangeEditValue,
    handleSaveEdit,
    handleStartEdit,
    isSaving,
  } = useApplicantFitCheckValueEdit()

  const match = getApplicantFitCheckMatch(applicantData)

  const { handleReevaluate, isReevaluating } = useApplicantFitCheckReevaluate()

  const hasRequestRow = getHasApplicantFitCheckRequestRow({
    applicantData,
    match,
  })

  const rulesUrl = getHasApplicantFitCheckRulesLink({ applicantData, match })
    ? getApplicantFitCheckRulesUrl(applicantData.matched_listing)
    : null

  function handleToggleBreakdown() {
    setBreakdownOpen(current => !current)
  }

  return (
    <Widget
      headerProps={{
        contentProps: {
          Title: ApplicantFitCheckBlockTitle,
          actionsProps: { EditButton: null },
          titleProps: {
            isReadOnly: isPreview,
            isReevaluating,
            match,
            onReevaluate: handleReevaluate,
            onToggleBreakdown: handleToggleBreakdown,
          },
        },
      }}
      isSkeleton={!isInitiated || !applicantData}
      qaName={QA_NAME_APPLICANT_FIT_CHECK}
      title={t('RentalPortal.ApplicantFitCheckTitleKey')}
    >
      {match?.unavailable_reason ? (
        <span className={styles.unavailable}>
          <Text isSecondary>
            {t(getApplicantFitCheckUnavailableKey(match))}
          </Text>
          <Link
            to={getApplicantMatchedListingUrl(applicantData?.matched_listing)}
          >
            {t('RentalPortal.MatchUnavailableListingLinkKey')}
          </Link>
        </span>
      ) : match ? (
        <div className={styles.body}>
          <FloatingTooltip
            content={getApplicantFitCheckMetaTooltip({ match, t })}
          >
            <Text
              {...getQaSelector({
                value: QA_NAME_APPLICANT_FIT_CHECK_SCORE_LINE,
              })}
              isSecondary
              className={styles.score}
            >
              {getApplicantFitCheckMetaLine({ match, t })}
            </Text>
          </FloatingTooltip>
          {isBreakdownOpen && match.breakdown ? (
            <ApplicantFitCheckBlockBreakdown match={match} />
          ) : null}
          <div
            className={classNames(styles.criteria, {
              [styles.stale]: match.stale,
            })}
          >
            <Text isSecondary className={styles.columnTitle}>
              {t('RentalPortal.MatchColumnCriterionKey')}
            </Text>
            <Text isSecondary className={styles.columnTitle}>
              {t('RentalPortal.MatchColumnRequirementKey')}
            </Text>
            <Text isSecondary className={styles.columnTitle}>
              {t('RentalPortal.MatchColumnApplicantKey')}
              <IconWithTooltip
                Icon={InformationIcon}
                className={styles.hintIcon}
                size={ICON_HEIGHT_16}
                svgSize={ICON_HEIGHT_16}
                tooltip={<RentalPortalMatchProvenanceLegend />}
              />
            </Text>
            <Text isSecondary className={styles.columnTitle}>
              {t('RentalPortal.MatchColumnFitKey')}
            </Text>
            <span className={styles.headerRule} />
            {getMatchCriteria(match).map(criterion => {
              const suggestion = getApplicantFitCheckSuggestion({
                criterion,
                match,
              })

              return (
                <ApplicantFitCheckBlockRow
                  criterion={criterion}
                  currency={match.currency}
                  editValue={editValue}
                  isEditing={editingField === criterion.applicant_field}
                  isReadOnly={isPreview}
                  isSaving={isSaving}
                  isSubmitting={submittingField === suggestion?.field}
                  key={criterion.key}
                  suggestion={suggestion}
                  onAcceptSuggestion={() => handleAcceptSuggestion(suggestion)}
                  onCancelEdit={handleCancelEdit}
                  onChangeEditValue={handleChangeEditValue}
                  onDismissSuggestion={() =>
                    handleDismissSuggestion(suggestion)
                  }
                  onSaveEdit={() => handleSaveEdit(criterion)}
                  onStartEdit={() => handleStartEdit(criterion)}
                />
              )
            })}
          </div>
          {(hasRequestRow && !isPreview) || rulesUrl ? (
            <ApplicantFitCheckBlockRequestRow
              hasRequest={hasRequestRow && !isPreview}
              match={match}
              rulesUrl={rulesUrl}
              onRequest={() => handleOpenRequestModal(match)}
            />
          ) : null}
        </div>
      ) : (
        <Text isSecondary>{t('RentalPortal.MatchNotEvaluatedKey')}</Text>
      )}
    </Widget>
  )
}

export default ApplicantFitCheckBlock
